/**
 * Activity Logs API Service
 * Handles retrieval of project, issue, and test case activity logs
 */

import { api } from './api';
import type { ProjectLogEntry, IssueLogEntry, TestCaseLogEntry, MappingHistoryEntry } from '../types/domain';

export interface ActivityLogsParams {
  limit?: number;
  page?: number;
  page_size?: number;
  cursor?: string;
  actor?: string | string[];
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface ActivityLogsPagination {
  page: number;
  page_size: number;
  total: number | null;
  total_pages: number | null;
  has_more: boolean;
  next_cursor: string | null;
}

export interface ActivityLogsFilterOptions {
  actors: string[];
  actions: string[];
}

function _buildLogParams(params?: ActivityLogsParams): URLSearchParams {
  const qs = new URLSearchParams();

  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.page != null) qs.set('page', String(params.page));
  if (params?.page_size != null) qs.set('page_size', String(params.page_size));
  if (params?.cursor) qs.set('cursor', params.cursor);
  // actor: repeated keys for multi-value so commas in values round-trip.
  if (params?.actor != null) {
    if (Array.isArray(params.actor)) {
      for (const a of params.actor) qs.append('actor', a);
    } else {
      qs.set('actor', params.actor);
    }
  }
  if (params?.search) qs.set('search', params.search);
  if (params?.date_from) qs.set('date_from', params.date_from);
  if (params?.date_to) qs.set('date_to', params.date_to);
  return qs;
}

export const activityLogsApi = {
  getProjectLogs: async (
    projectId: string,
    params: ActivityLogsParams | number = 20,
  ): Promise<{ logs: ProjectLogEntry[]; count: number; pagination?: ActivityLogsPagination; filter_options?: ActivityLogsFilterOptions }> => {
    const normalized = typeof params === 'number' ? { limit: params } : params;
    const qs = _buildLogParams(normalized);
    return api.get(`/admin/projects/${projectId}/project-logs?${qs}`);
  },

  getIssueLogs: async (
    projectId: string,
    params: ActivityLogsParams | number = 20,
  ): Promise<{ logs: IssueLogEntry[]; count: number; pagination?: ActivityLogsPagination; filter_options?: ActivityLogsFilterOptions }> => {
    const normalized = typeof params === 'number' ? { limit: params } : params;
    const qs = _buildLogParams(normalized);
    return api.get(`/admin/projects/${projectId}/issue-logs?${qs}`);
  },

  getTestCaseLogs: async (
    projectId: string,
    params: ActivityLogsParams | number = 20,
  ): Promise<{ logs: TestCaseLogEntry[]; count: number; pagination?: ActivityLogsPagination; filter_options?: ActivityLogsFilterOptions }> => {
    const normalized = typeof params === 'number' ? { limit: params } : params;
    const qs = _buildLogParams(normalized);
    return api.get(`/admin/projects/${projectId}/test-case-logs?${qs}`);
  },

  getMappingHistory: async (projectId: string, limit = 50): Promise<{ history: MappingHistoryEntry[]; count: number; project_id: string }> => {
    const params = new URLSearchParams({ project_id: projectId, limit: String(limit) });
    return api.get<{ history: MappingHistoryEntry[]; count: number; project_id: string }>(`/admin/user-mappings/history?${params}`);
  },

  updateMapping: async (data: {
    project_id: string;
    subdomain: string;
    area: string;
    user_ids?: string[];
    person_names?: string[];
    person_emails?: string[];
  }) => {
    return api.put<{ mapping_id: string }>('/admin/user-mappings', data);
  },

  deleteMapping: async (projectId: string, subdomain: string, area: string) => {
    const params = new URLSearchParams({ project_id: projectId, subdomain, area });
    return api.delete<{ message: string }>(`/admin/user-mappings?${params}`);
  },
};
